/**
 * FusionCaseSummary — case stat strip for the Fusion Inspector empty state.
 * Surfaces live in Graph Legend; this is nodes/links/evidence counts only.
 * Demo sandbox uses the seed; real /cases/:id/canvas reads the live Cytoscape graph.
 */
import { useEffect, useState } from 'react';
import type cytoscape from 'cytoscape';
import { computeStats } from '../../data/fusionStats';
import { useFusionStore } from '../../stores/useFusionStore';
import { CaseSummary, type CaseStat } from '../cases/CaseSummary';

type Overview = {
  nodeCount: number;
  edgeCount: number;
  deterministicEdges: number;
  scoredEdges: number;
  handoverCount: number;
  valueByUnit: Record<string, number>;
};

function liveOverviewFromCanvas(): Overview | null {
  const cy = (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
  if (!cy || (typeof cy.destroyed === 'function' && cy.destroyed())) return null;

  const nodes = cy.nodes().filter((n) => n.visible());
  const edges = cy.edges().filter((e) => e.visible());
  let deterministicEdges = 0;
  let scoredEdges = 0;
  let handoverCount = 0;
  const valueByUnit: Record<string, number> = {};

  edges.forEach((e) => {
    const det = String(e.data('determinism') || 'deterministic');
    if (det === 'scored') scoredEdges += 1;
    else deterministicEdges += 1;
    const value = e.data('value');
    const unit = e.data('unit');
    if (typeof value === 'number' && value && typeof unit === 'string' && unit) {
      valueByUnit[unit] = (valueByUnit[unit] || 0) + value;
    }
    const srcMod = String(e.source().data('module') || '');
    const tgtMod = String(e.target().data('module') || '');
    if (srcMod && tgtMod && srcMod !== tgtMod) handoverCount += 1;
  });

  return {
    nodeCount: nodes.length,
    edgeCount: edges.length,
    deterministicEdges,
    scoredEdges,
    handoverCount,
    valueByUnit,
  };
}

function toCaseStats(s: Overview): CaseStat[] {
  const tracedValue = Object.entries(s.valueByUnit)
    .map(([u, v]) => `${v >= 100000 ? (v / 100000).toFixed(1) + 'L' : v.toLocaleString()} ${u}`).join(' · ') || '—';

  return [
    { label: 'Nodes', value: s.nodeCount },
    { label: 'Links', value: s.edgeCount },
    { label: 'Deterministic', value: s.deterministicEdges, sub: 'verified' },
    { label: 'Scored', value: s.scoredEdges, sub: 'leads' },
    { label: 'Handovers', value: s.handoverCount, sub: 'cross-surface' },
    { label: 'Traced value', value: tracedValue },
  ];
}

const EMPTY: Overview = {
  nodeCount: 0,
  edgeCount: 0,
  deterministicEdges: 0,
  scoredEdges: 0,
  handoverCount: 0,
  valueByUnit: {},
};

export function FusionCaseSummary({
  layout = 'stack',
  className,
  /** seed = /fusion demo; canvas = live Cytoscape on a real case */
  source = 'seed',
}: {
  layout?: 'row' | 'stack';
  className?: string;
  source?: 'seed' | 'canvas';
} = {}) {
  const step = useFusionStore((s) => s.step);
  const [live, setLive] = useState<Overview | null>(null);

  useEffect(() => {
    if (source !== 'canvas') return;
    const tick = () => setLive(liveOverviewFromCanvas());
    tick();
    const id = window.setInterval(tick, 800);
    return () => window.clearInterval(id);
  }, [source]);

  let overview: Overview;
  if (source === 'canvas') {
    overview = live ?? EMPTY;
  } else {
    const s = computeStats(step);
    overview = {
      nodeCount: s.nodeCount,
      edgeCount: s.edgeCount,
      deterministicEdges: s.deterministicEdges,
      scoredEdges: s.scoredEdges,
      handoverCount: s.handovers.length,
      valueByUnit: s.valueByUnit,
    };
  }

  return <CaseSummary stats={toCaseStats(overview)} layout={layout} className={className} />;
}
